import {
  IncomeBox,
} from '@/components/ui/molecules';
import { LoadingSpinner, RowSpacer } from '../atoms';
import {
  OrderDirectionOption,
  PageSizeOption,
  useGetDashboardDataQuery,
  useGetExpensesReportQuery,
  useGetOwnerContractsForListQuery,
} from '@/graphql/generated';
import { useErrorHandlerContext } from '@/customHooks';
import {
  DashboardIncidentsByStatusCard,
  DashboardContractsCard,
  DashboardOccupationCard,
  DashboardStatsSection,
  DashboardCalendar,
  DashboardIncomeAndExpensesCard,
  DashboardAlertNoticeCard,
  DashboardManagerRatingsCard,
} from '../organism';
import { DashboardPropertyFilterBar } from '../organism/DashboardPropertyFilterBar';
import { useMemo } from 'react';
import { DashboardFilterProvider, useDashboardFilter } from '@/contexts/DashboardFilterContext';

const parseAmount = (value: string | null | undefined): number => {
  if (!value) return 0;
  const parsed = parseFloat(String(value).replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrency = (value: number): string => (
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value)
);

const formatPercentage = (value: number): string => {
  const absValue = Math.abs(value);
  return `${value >= 0 ? '+' : '-'}${absValue.toFixed(1)}%`;
};

export const DashboardTemplate = () => (
  <DashboardFilterProvider>
    <DashboardTemplateInner />
  </DashboardFilterProvider>
);

function DashboardTemplateInner() {
  const { dashboardFilter, selectedPropertyId } = useDashboardFilter();
  const { data, loading, error } = useGetDashboardDataQuery({
    variables: dashboardFilter ? { filter: dashboardFilter } : {},
    fetchPolicy: 'network-only',
  });
  const { data: ownerContractsData, error: ownerContractsError } = useGetOwnerContractsForListQuery({
    variables: {
      filterInput: {
        pageSize: PageSizeOption.OneHundred,
        page: '1',
        orderDirection: OrderDirectionOption.Desc,
      },
    },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  const { data: expensesData, error: expensesError } = useGetExpensesReportQuery({
    variables: {
      filterInput: {
        pageSize: PageSizeOption.OneHundred,
        page: '1',
        orderDirection: OrderDirectionOption.Desc,
        ...(selectedPropertyId ? { propertyId: selectedPropertyId } : {}),
      },
    },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const { open: errorHandle } = useErrorHandlerContext();

  const incomeBoxes = useMemo(() => {
    const incomeTotal = parseAmount(data?.dashboardData?.incomeAndExpenses?.income?.total);

    const ownerItems = (ownerContractsData?.ownerContracts?.items ?? []).filter(
      (c) => !selectedPropertyId || c.property.id === selectedPropertyId,
    );

    const ownerContractsTotal = ownerItems.reduce(
      (acc, contract) => acc + parseAmount(contract.contractAmount),
      0,
    );

    const expensesReportTotal = (expensesData?.expensesReport?.items ?? []).reduce(
      (acc, expense) => acc + parseAmount(expense.expenseCost),
      0,
    );

    const expensesTotal = ownerContractsTotal + expensesReportTotal;
    const benefitsTotal = incomeTotal - expensesTotal;

    const incomePercentage = data?.dashboardData?.incomeAndExpenses?.income?.collectedPercentage ?? 0;
    const expensesPercentage = incomeTotal > 0 ? (expensesTotal * 100) / incomeTotal : 0;
    const benefitsPercentage = incomeTotal > 0 ? (benefitsTotal * 100) / incomeTotal : 0;

    return [
      {
        variant: 'income' as const,
        amount: formatCurrency(incomeTotal),
        percentage: formatPercentage(incomePercentage),
        isPositive: true,
      },
      {
        variant: 'expenses' as const,
        amount: formatCurrency(expensesTotal),
        percentage: formatPercentage(-expensesPercentage),
        isPositive: false,
      },
      {
        variant: 'benefit' as const,
        amount: formatCurrency(benefitsTotal),
        percentage: formatPercentage(benefitsPercentage),
        isPositive: benefitsPercentage >= 0,
      },
    ];
  }, [
    data?.dashboardData?.incomeAndExpenses?.income?.collectedPercentage,
    data?.dashboardData?.incomeAndExpenses?.income?.total,
    expensesData?.expensesReport?.items,
    ownerContractsData?.ownerContracts?.items,
    selectedPropertyId,
  ]);

  if (error) errorHandle(error);
  if (ownerContractsError) errorHandle(ownerContractsError);
  if (expensesError) errorHandle(expensesError);

  return (
    <div className="mx-auto w-full max-w-7xl px-4 pb-10 pt-2 sm:px-6">
      <RowSpacer height={3} />
      {loading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {incomeBoxes.map((income, index) => (
              <div key={index}>
                <IncomeBox {...income} />
              </div>
            ))}
          </div>
          <RowSpacer height={3} />
          <DashboardStatsSection data={data?.dashboardData.stats} />
          <RowSpacer height={2} />
          <div className="w-full">
            <DashboardPropertyFilterBar />
          </div>
          <RowSpacer height={3} />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="flex min-w-0 flex-col gap-6">
              <DashboardIncidentsByStatusCard data={data?.dashboardData.incidentsByStatus} />
              <DashboardManagerRatingsCard />
            </div>
            <div className="min-w-0">
              <DashboardOccupationCard data={data?.dashboardData.occupation} />
            </div>
          </div>
          <RowSpacer height={3} />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="min-w-0">
              <DashboardAlertNoticeCard />
            </div>
            <div className="min-w-0">
              <DashboardContractsCard />
            </div>
          </div>
          <RowSpacer height={3} />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="min-w-0">
              <DashboardIncomeAndExpensesCard data={data?.dashboardData?.incomeAndExpenses} />
            </div>
            <div className="min-w-0">
              <DashboardCalendar />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
